/**
 * Producer Validation Schemas - AgroProj Convex Backend
 *
 * Zod validation schemas for producer forms and data validation
 */

import { z } from "zod";

// Base producer schema (matches Convex schema structure)
export const producerBaseSchema = z.object({
  name: z.string().optional(),
  companyName: z.string().optional(),
  tradingName: z.string().optional(),
  documentType: z.union([z.literal("cpf"), z.literal("cnpj")]),
  documentValue: z.string(),
  phone1: z.string().optional(),
  phone2: z.string().optional(),
  email: z.string().email("Email inválido").optional(),
  observation: z.string().optional(),
  status: z.union([z.literal("active"), z.literal("inactive")]).optional(),
});

export type ProducerBaseSchema = z.infer<typeof producerBaseSchema>;

// Producer Person Form Schema (CPF)
export const producerPersonFormSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  documentValue: z.string().min(1, "CPF é obrigatório"),
  phone1: z.string().optional(),
  phone2: z.string().optional(),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  observation: z.string().optional(),
});

export type ProducerPersonFormSchema = z.infer<typeof producerPersonFormSchema>;

// Producer Company Form Schema (CNPJ)
export const producerCompanyFormSchema = z.object({
  companyName: z.string().min(1, "Razão social é obrigatória"),
  tradingName: z.string().optional(),
  documentValue: z.string().min(1, "CNPJ é obrigatório"),
  phone1: z.string().optional(),
  phone2: z.string().optional(),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  observation: z.string().optional(),
});

export type ProducerCompanyFormSchema = z.infer<typeof producerCompanyFormSchema>;

const geoJsonPositionSchema = z.array(z.number()).min(2);
const geoJsonLinearRingSchema = z.array(geoJsonPositionSchema).min(4);
const geoJsonPolygonCoordinatesSchema = z.array(geoJsonLinearRingSchema).min(1);
const geoJsonMultiPolygonCoordinatesSchema = z.array(geoJsonPolygonCoordinatesSchema).min(1);

export const ruralPropertyBoundaryGeoJsonSchema = z.object({
  type: z.literal("FeatureCollection"),
  features: z
    .array(
      z.object({
        type: z.literal("Feature"),
        properties: z.object({
          name: z.string().optional(),
          description: z.string().optional(),
        }),
        geometry: z.union([
          z.object({
            type: z.literal("Polygon"),
            coordinates: geoJsonPolygonCoordinatesSchema,
          }),
          z.object({
            type: z.literal("MultiPolygon"),
            coordinates: geoJsonMultiPolygonCoordinatesSchema,
          }),
        ]),
      }),
    )
    .min(1),
});

export const ruralPropertyBoundaryBoundsSchema = z.object({
  north: z.number(),
  south: z.number(),
  east: z.number(),
  west: z.number(),
});

export type RuralPropertyBoundaryGeoJsonSchema = z.infer<typeof ruralPropertyBoundaryGeoJsonSchema>;
export type RuralPropertyBoundaryBoundsSchema = z.infer<typeof ruralPropertyBoundaryBoundsSchema>;

export const ruralRegistryReferenceSchema = z.object({
  kind: z.union([z.literal("cib"), z.literal("nirf")]),
  value: z
    .string()
    .trim()
    .refine((value) => {
      const length = value.replace(/\D/g, "").length;
      return length >= 6 && length <= 14;
    }, "CIB ou NIRF inválido"),
});

export type RuralRegistryReferenceSchema = z.infer<typeof ruralRegistryReferenceSchema>;

// Rural Property Schema
export const ruralPropertyFormSchema = z
  .object({
    registration: z.string().min(1, "Matrícula é obrigatória"),
    title: z.string().min(1, "Nome/Descrição é obrigatório"),
    state: z.string().min(1, "Estado é obrigatório"),
    city: z.string().min(1, "Cidade é obrigatória"),
    totalArea: z
      .number({ error: "Informe um número válido em Área total" })
      .positive("Área total deve ser maior que zero"),
    exploredArea: z.number().min(0, "Área explorada não pode ser negativa"),
    situation: z.union([
      z.literal("rented"),
      z.literal("owned"),
      z.literal("third_party"),
      z.literal("leased"),
      z.literal("loaned"),
      z.literal("settlement"),
      z.literal("lease"), // Add lease option for frontend compatibility
      z.literal("own"), // Add own option for frontend compatibility
    ]),

    // Assessment information
    assessmentReport: z.boolean(),
    assessmentReportDate: z.union([z.date(), z.number()]).optional(), // Accept both Date and timestamp
    assessmentReportValue: z
      .number({ error: "Informe um número válido em Valor da avaliação" })
      .min(0, "Valor da avaliação não pode ser negativo")
      .optional(),

    // Area destination
    destinationOfTheArea: z.string().optional(),
    destinationOfTheAreaId: z.string().optional(),
    destinationOfTheArea1Type: z.string().optional(),
    destinationOfTheArea1Value: z.string().optional(),
    destinationOfTheArea2Type: z.string().optional(),
    destinationOfTheArea2Value: z.string().optional(),
    destinationOfTheArea3Type: z.string().optional(),
    destinationOfTheArea3Value: z.string().optional(),
    ruralRegistryReference: ruralRegistryReferenceSchema.optional(),

    // Lease information
    leaseContract: z.boolean().optional(),
    leaseDocumentType: z.string().optional(),
    leaseDocumentValue: z.string().optional(), // CPF/CNPJ value
    leaseStartDate: z.union([z.date(), z.number()]).optional(), // Accept both Date and timestamp
    leaseEndDate: z.union([z.date(), z.number()]).optional(), // Accept both Date and timestamp
    leaseDueDate: z.union([z.date(), z.number()]).optional(), // Accept both Date and timestamp
    monthlyRentValue: z.number().min(0, "Valor mensal não pode ser negativo").optional(),
    annualRentValue: z.number().min(0, "Valor anual não pode ser negativo").optional(),
    landOwnerName: z.string().optional(),
    landOwnerDocument: z.string().optional(),
    leaseFullNameOwner: z.string().optional(), // Full name of lease owner

    // Ownership information
    ownPercentage: z
      .number({ error: "Informe um número válido em Percentual da propriedade" })
      .min(0, "Percentual da propriedade deve estar entre 0 e 100")
      .max(100, "Percentual da propriedade deve estar entre 0 e 100")
      .optional(), // Ownership percentage

    // Financial information
    subjectToWarranty: z.boolean().optional(), // Can be used as warranty/collateral

    // Imported geospatial boundary
    boundaryGeoJson: ruralPropertyBoundaryGeoJsonSchema.optional(),
    boundarySourceFileName: z.string().optional(),
    boundarySourceFileType: z
      .union([z.literal("kml"), z.literal("kmz"), z.literal("manual"), z.literal("car")])
      .optional(),
    boundaryAreaHectares: z.number().min(0).optional(),
    boundaryBounds: ruralPropertyBoundaryBoundsSchema.optional(),
    boundaryImportedAt: z.number().optional(),

    // Producer relationship
    producerId: z.string(), // ID reference

    // Frontend helper fields (for form display)
    totalAreaString: z.string().optional(),
    ownPercentageString: z.string().optional(),
    assessmentReportOption: z.union([z.literal("yes"), z.literal("no")]).optional(),
    assessmentReportValueString: z.string().optional(),
    subjectToWarrantyOption: z.union([z.literal("yes"), z.literal("no")]).optional(),
    ruralRegistryKind: z.union([z.literal("cib"), z.literal("nirf")]).optional(),
    ruralRegistryValue: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    const hasDestinationId =
      typeof value.destinationOfTheAreaId === "string" &&
      value.destinationOfTheAreaId.trim().length > 0;

    const hasDestinationLabel =
      typeof value.destinationOfTheArea === "string" &&
      value.destinationOfTheArea.trim().length > 0;

    if (!hasDestinationId && !hasDestinationLabel) {
      ctx.addIssue({
        code: "custom",
        message:
          "A destinação da área é obrigatória. Selecione uma opção da lista ou informe o texto legado.",
        path: ["destinationOfTheArea"],
      });
    }

    const hasRegistryKind = value.ruralRegistryKind !== undefined;
    const registryValue = value.ruralRegistryValue?.trim() ?? "";
    const hasRegistryValue = registryValue.length > 0;
    if (hasRegistryKind !== hasRegistryValue) {
      ctx.addIssue({
        code: "custom",
        message: "Informe o tipo e o número do cadastro rural",
        path: [hasRegistryKind ? "ruralRegistryValue" : "ruralRegistryKind"],
      });
    } else if (hasRegistryKind && hasRegistryValue) {
      const result = ruralRegistryReferenceSchema.safeParse({
        kind: value.ruralRegistryKind,
        value: registryValue,
      });
      if (!result.success) {
        ctx.addIssue({
          code: "custom",
          message: `${value.ruralRegistryKind === "cib" ? "CIB" : "NIRF"} inválido`,
          path: ["ruralRegistryValue"],
        });
      }
    }
  });

export type RuralPropertyFormSchema = z.infer<typeof ruralPropertyFormSchema>;

// Urban Property Schema
export const urbanPropertyFormSchema = z.object({
  registration: z.string().min(1, "Registro é obrigatório"),
  title: z.string().min(1, "Título é obrigatório"),
  state: z.string().min(1, "Estado é obrigatório"),
  city: z.string().min(1, "Cidade é obrigatória"),
  situation: z.union([z.literal("rented"), z.literal("owned"), z.literal("third_party")]),
  assessmentReport: z.boolean(),
  assessmentReportDueDate: z.number().optional(),
  assessmentReportValue: z.number().min(0).optional(),
  subjectToWarranty: z.boolean(),
});

export type UrbanPropertyFormSchema = z.infer<typeof urbanPropertyFormSchema>;

// Animal Schema
export const animalFormSchema = z
  .object({
    specieId: z.string().min(1, "Espécie é obrigatória"),
    descriptionId: z.string().optional(),
    descriptionFreeText: z.string().optional(),
    ageId: z.string().optional(),
    race: z.string().optional(),
    quantity: z.number().positive("Quantidade deve ser maior que zero"),
    unitPrice: z.number().min(0, "Preço unitário não pode ser negativo"),
    subjectToWarranty: z.boolean(),
    producerId: z.string().min(1, "ID do produtor é obrigatório"),
    // Helper fields for form handling
    quantityString: z.string().optional(),
    unitPriceString: z.string().optional(),
    subjectToWarrantyOption: z.string().optional(),
    totalPrice: z.number().optional(),
    totalPriceString: z.string().optional(),
  })
  .refine(
    (value) =>
      (value.descriptionId?.trim().length ?? 0) > 0 ||
      (value.descriptionFreeText?.trim().length ?? 0) > 0,
    { message: "Tipo do animal é obrigatório", path: ["descriptionId"] },
  );

export type AnimalFormSchema = z.infer<typeof animalFormSchema>;

// Machine Schema
export const machineFormSchema = z.object({
  title: z.string().min(1, "Título é obrigatório"),
  type: z.union([z.literal("improvement"), z.literal("machine")]),
  machineBrand: z.string().optional(),
  machineYear: z.number().int().min(1900).max(new Date().getFullYear()).optional(),
  conservationState: z.union([z.literal("good"), z.literal("regular"), z.literal("excellent")]),
  assessmentValue: z.number().min(0).optional(),
  subjectToWarranty: z.boolean(),
});

export type MachineFormSchema = z.infer<typeof machineFormSchema>;

// Contact Schema
export const contactFormSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  role: z.string().optional(),
  phone1: z.string().optional(),
  phone2: z.string().optional(),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  observation: z.string().optional(),
});

export type ContactFormSchema = z.infer<typeof contactFormSchema>;

// Income/Expense Schemas
export const incomeFormSchema = z.object({
  year: z
    .number()
    .int()
    .min(2000)
    .max(new Date().getFullYear() + 1),
  activityId: z.string().min(1, "Atividade é obrigatória"),
  quantity: z.number().min(0, "Quantidade não pode ser negativa"),
  unitPrice: z.number().min(0, "Preço unitário não pode ser negativo"),
  totalValue: z.number().min(0, "Valor total não pode ser negativo"),
  observation: z.string().optional(),
});

export type IncomeFormSchema = z.infer<typeof incomeFormSchema>;

// Producer Income Form Schemas (with helper fields for forms)
export const producerIncomeFormBaseSchema = z.object({
  year: z
    .number()
    .int()
    .min(2000)
    .max(new Date().getFullYear() + 1),
  activityId: z.string().min(1, "Atividade é obrigatória"),
  quantity: z.number().min(0, "Quantidade não pode ser negativa"),
  unitPrice: z.number().min(0, "Preço unitário não pode ser negativo"),
  totalValue: z.number().min(0, "Valor total não pode ser negativo"),
  observation: z.string().optional(),
  type: z.union([z.literal("expected"), z.literal("actual")]),
  harvestSeason: z.string().optional(),
  projectType: z.string().optional(),
});

export type ProducerIncomeFormBaseSchema = z.infer<typeof producerIncomeFormBaseSchema>;

// Extended form schema with helper fields for UI
export const producerIncomeFormSchema = producerIncomeFormBaseSchema.extend({
  producerId: z.string().min(1, "Produtor é obrigatório"),
  quantityString: z.string(),
  unitPriceString: z.string(),
  totalValueString: z.string(),
});

export type ProducerIncomeFormSchema = z.infer<typeof producerIncomeFormSchema>;

export const expenseFormSchema = z.object({
  year: z
    .number()
    .int()
    .min(2000)
    .max(new Date().getFullYear() + 1),
  activityId: z.string().min(1, "Atividade é obrigatória"),
  quantity: z.number().min(0, "Quantidade não pode ser negativa"),
  unitPrice: z.number().min(0, "Preço unitário não pode ser negativo"),
  totalValue: z.number().min(0, "Valor total não pode ser negativo"),
  observation: z.string().optional(),
});

export type ExpenseFormSchema = z.infer<typeof expenseFormSchema>;

// Address Schema
export const addressFormSchema = z.object({
  street: z.string().trim().min(1, "Logradouro é obrigatório"),
  street2: z.string().optional(),
  number: z.string().trim().min(1, "Número é obrigatório"),
  neighborhood: z.string().trim().min(1, "Bairro é obrigatório"),
  city: z.string().trim().min(1, "Cidade é obrigatória"),
  state: z.enum(
    [
      "AC",
      "AL",
      "AP",
      "AM",
      "BA",
      "CE",
      "DF",
      "ES",
      "GO",
      "MA",
      "MT",
      "MS",
      "MG",
      "PA",
      "PB",
      "PR",
      "PE",
      "PI",
      "RJ",
      "RN",
      "RS",
      "RO",
      "RR",
      "SC",
      "SP",
      "SE",
      "TO",
    ],
    { error: "Selecione um estado brasileiro" },
  ),
  postalCode: z
    .string()
    .trim()
    .min(1, "CEP é obrigatório")
    .regex(/^\d{5}-?\d{3}$/, "CEP deve conter exatamente 8 dígitos"),
  country: z.string().default("Brasil"),
});

export type AddressFormSchema = z.infer<typeof addressFormSchema>;
